import { normalizeDashboards } from '../../../../utils'
import { ensureUniqueObjectIds } from '@edge/spark/utils'
import { getActiveDashboardId } from '../../../../Stores'
import {
    ClientUserDashboardsDocument as query,
    type ClientUserDashboardsQuery,
    type MutationResolvers,
    UpdateUserDashboardsDocument as mutation,
    type UpdateUserDashboardsMutation,
} from '../../../../generated/graphql'
import { DashboardCardDef } from '../../../../types'
import { createDialog } from '@edge/spark/feature-dialog'
import type { ApolloResolverContext } from '@edge/spark/io-apollo'
import { getActiveAuctionId } from '@edge/spark/io-roles'

const updateDashboardCard: MutationResolvers<ApolloResolverContext>['updateDashboardCard'] = async (
    _root,
    { card, cardId, dashId, userId },
    { cache, client },
) => {
    console.debug('io-dashboards updateDashboardCard Deprecated, refactor (soon)')

    if (!client) {
        throw new Error('Missing ApolloClient')
    }

    const auctionId = getActiveAuctionId()

    if (!auctionId) {
        throw new Error('updateDashboardCard: Missing AuctionId')
    }

    if (userId && !dashId) {
        throw new Error('Custom userId without dashId')
    }

    const { data } = await client.query<ClientUserDashboardsQuery>({
        query,
        variables: { userId },
        fetchPolicy: 'network-only',
    })
    const activeDashboardId = getActiveDashboardId()
    const savedDashboards = normalizeDashboards(data?.dashboards)
    const dashboardIdToUpdate = dashId || activeDashboardId
    const dashboardToUpdateIndex = savedDashboards.findIndex((d) => d.id === dashboardIdToUpdate)
    const dashboardToUpdate = savedDashboards[dashboardToUpdateIndex]

    if (!dashboardToUpdate) {
        createDialog({
            action: 'OK',
            message: `Error: Dashboard with id:${dashboardIdToUpdate} not found`,
            timeout: 5000,
        })

        return null
    }

    const cardToUpdateIndex = dashboardToUpdate.cards.findIndex((c) => c.id === cardId)
    const cardIdMatch = cardToUpdateIndex !== -1

    if (!cardIdMatch) {
        throw new Error(`CardId MisMatch ${JSON.stringify({ userId, dashId, cardId })}`)
    }

    const cardDef = JSON.parse(card) as DashboardCardDef
    const updatedCard = {
        ...cardDef,
        id: cardId,
    }
    const updatedCards = [
        ...dashboardToUpdate.cards.slice(0, cardToUpdateIndex),
        updatedCard,
        ...dashboardToUpdate.cards.slice(cardToUpdateIndex + 1),
    ]
    const cleanedCards = ensureUniqueObjectIds(updatedCards)

    const updatedDashboard = { ...dashboardToUpdate, cards: cleanedCards }
    const updatedDashboards = [
        ...savedDashboards.slice(0, dashboardToUpdateIndex),
        updatedDashboard,
        ...savedDashboards.slice(dashboardToUpdateIndex + 1),
    ]
    const cleanedDashboards = ensureUniqueObjectIds(updatedDashboards)
    const stringifiedUpdatedDashboards = cleanedDashboards.map((d) => JSON.stringify(d))

    const admin = userId ? { auctionId, subjectUserId: userId } : undefined

    await client.mutate<UpdateUserDashboardsMutation>({
        mutation,
        variables: { admin, dashboards: stringifiedUpdatedDashboards },
        optimisticResponse: {
            updateUser: {
                __typename: 'UserMutationResponse',
                authenticationId: userId || null,
                statusCode: 200,
                statusMessage: 'Updated dashboards.',
            },
        },
        update: (store): void => {
            if (userId) {
                store.writeData({
                    data: {
                        [`dashboards({"userId":"${userId}"})`]: stringifiedUpdatedDashboards,
                        administrator: {
                            __typename: 'Administrator',
                            [`user({"input":{"admin":{"auctionId":"${auctionId}","subjectUserId":"${userId}"}}})`]: {
                                __typename: 'User',
                                dashboards: stringifiedUpdatedDashboards,
                            },
                        },
                    },
                })
            } else {
                store.writeData({
                    data: {
                        'dashboards({})': stringifiedUpdatedDashboards,
                        user: {
                            __typename: 'User',
                            dashboards: stringifiedUpdatedDashboards,
                        },
                    },
                })
            }
        },
    })

    return true
}

export default updateDashboardCard
